/** @jsxImportSource preact */
import type { ComponentChildren } from "preact";

import { formVars } from "../form/form-styles";
import { PanelSheetClose, PanelSheetLayer } from "../sheet/panel-sheet";
import type { WidgetTokens } from "../theme";

/**
 * 请求浮层的样式。浮层底色是托盘色，输入框、预览这些白色纸面直接放在上面，所以里面的采集卡、预览卡
 * 收掉自己的托盘、内边距和同名标题，标题由浮层顶上那一行给。标题行固定，下面的内容超出时只滚这一块。
 * 没有标题行的状态页，关闭键浮在右上角。
 */
export const REQUEST_OVERLAY_STYLES = `
.gn-request-head { flex:none; display:flex; align-items:center; gap:12px; margin:-8px -8px 0 0; }
.gn-request-title { flex:1 1 0; min-width:0; margin:0; font-size:16px; font-weight:700; letter-spacing:-.01em; line-height:1.3; }
.gn-request-close { position:absolute; z-index:1; top:8px; right:8px; }
.gn-request-body { box-sizing:border-box; min-height:0; margin:0 -16px -16px; padding:0 16px 16px; overflow-y:auto; overscroll-behavior:contain; scrollbar-width:thin; scrollbar-color:var(--gn-form-line) transparent; }
.gn-request-body:focus-visible { outline:2px solid var(--gn-form-ring); outline-offset:-2px; }
.gn-request-body .gn-card { max-width:none; padding:0; background:transparent; }
.gn-request-body .gn-card > .gn-card-head { display:none; }
`;

export interface RequestOverlayProps {
  tokens: WidgetTokens;
  /** 聊天窗的实底色：托盘色在深色和毛玻璃上是半透明的，叠在它上面才不会透出后面的消息。 */
  background: string;
  /** 触屏：关闭键和按钮放大。 */
  touch: boolean;
  /** 表单和预览的标题；状态页不给，标题在状态里。 */
  title?: string;
  /** 给对话框命名的标题 id：有 title 时是这一行，没有时由 RequestStatus 的标题带上。 */
  titleId: string;
  /** 发送中：不能关，关闭键置灰，遮罩和 Esc 都不理。 */
  locked: boolean;
  onDismiss: () => void;
  children: ComponentChildren;
}

/**
 * 联系商家的请求浮层：所有入口打开的都是这一个，和加购面板一样从聊天窗底部升起，最高占 88%。
 * 加载、填写、预览、发送、受理、失败都在这一个浮层里切换，消息流里不出卡。
 */
export function RequestOverlay({
  tokens: t,
  background,
  touch,
  title,
  titleId,
  locked,
  onDismiss,
  children,
}: RequestOverlayProps) {
  const close = <PanelSheetClose disabled={locked} onClick={onDismiss} />;
  return (
    <PanelSheetLayer
      labelledBy={titleId}
      locked={locked}
      onDismiss={onDismiss}
      style={{
        ...formVars(t),
        "--gn-sheet-bg": `linear-gradient(var(--gn-form-tray), var(--gn-form-tray)), ${background}`,
      }}
      touch={touch}
    >
      <style>{REQUEST_OVERLAY_STYLES}</style>
      {title === undefined ? (
        <div class="gn-request-close">{close}</div>
      ) : (
        <div class="gn-request-head">
          <h2 class="gn-request-title" id={titleId}>
            {title}
          </h2>
          {close}
        </div>
      )}
      <div
        aria-labelledby={titleId}
        class="gn-request-body"
        role="region"
        tabIndex={title === undefined ? undefined : 0}
      >
        {children}
      </div>
    </PanelSheetLayer>
  );
}
